import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'
Vue.use(Vuex)

import activePropertySlides from './getters/activePropertySlides'

const isProd = process.env.VUE_APP_ENV === 'production'

// Data Paths
const configData = isProd ? '/data/config.json' : process.env.VUE_APP_API_HOST + '/config/touchscreen'
const navigationData = isProd ? '/data/navigation.json' : process.env.VUE_APP_API_HOST + '/navigation/touchscreen'
const pagesData = isProd ? '/data/pages.json' : process.env.VUE_APP_API_HOST + '/pages/touchscreen'
const livingOptionsData = isProd ? '/data/living-options.json' : process.env.VUE_APP_API_HOST + '/living-options/touchscreen'
const subsectionGroupsData = isProd ? '/data/subsection-groups.json' : process.env.VUE_APP_API_HOST + '/subsection-groups/touchscreen'
const subsectionsData = isProd ? '/data/subsections.json' : process.env.VUE_APP_API_HOST + '/subsections/touchscreen'
const propertiesData = isProd ? '/data/properties.json' : process.env.VUE_APP_API_HOST + '/properties/touchscreen'
const dwellingsData = isProd ? '/data/dwellings.json' : process.env.VUE_APP_API_HOST + '/dwellings/touchscreen'
const packagesData = isProd ? '/data/packages.json' : process.env.VUE_APP_API_HOST + '/packages/touchscreen'
const turnkeysData = isProd ? '/data/properties.turnkey.listing.json' : process.env.VUE_APP_API_HOST + '/properties/turnkey/listing'

// Store Modules
import property from './property.module'
import masterplan from './masterplan.module'
import animations from './animations.module'
import sounds from './sounds.module'
import sidebar from './sidebar.module'
import favourites from './favourites.module'
import interactions from './interactions.module'
import actions from './actions.module'
import filter from './filter.module'

export default new Vuex.Store({
  modules: {
    actions,
    animations,
    favourites,
    filter,
    interactions,
    masterplan,
    property,
    sounds,
    sidebar
  },

  state: {
    assetsPath: isProd ? '/assets/' : process.env.VUE_APP_ASSETS_HOST,
    version: process.env.VUE_APP_VERSION,
    deviceName: null,
    selector: null,

    config: [],
    navigation: [],
    pages: [],

    isBottomBarVisible: false,
    isMatterportAvailable: null,
    lightswarmHost: null,
    localIframePlaying: false,
    videoWallIframePlaying: false,
    videoWallHost: null,
    triggerVideos: false,
    hideLiveHere: false,
    appScale: 1,

    styleTemplate: {},

    language: { name: 'English', code: 'en', rtl: false },
    languages: [
      { name: 'English', code: 'en', rtl: false },
      { name: 'French', code: 'fr', rtl: false },
      { name: 'German', code: 'ge', rtl: false }
    ],

    activePackageSlideId: '',
    activeTagsOverride: null,
    activeBackgroundOverride: ''
  },

  getters: {
    activeCategory (state) {
      if (state.route.params.categoryId) {
        return `category/${state.route.params.categoryId}`
      }
    },

    activeBottomBar (state, getters) {
      const routeName = state.route.name

      if (routeName === 'rest') {
        return []
      }

      let activePageNavigationItem = null
      if (state.route.params.categoryId) {
        activePageNavigationItem = _.find(getters.navigation, category => {
          return category._id === state.route.params.categoryId
        })
      } else if (getters.navigation) {
        getters.navigation.forEach(category => {
          if (state.route.path.includes(category.customRoute)) {
            activePageNavigationItem = category
          }
        })
      }
      return activePageNavigationItem && activePageNavigationItem.children ? activePageNavigationItem.children : []
    },

    activeSubBottomBar (state, getters, rootState) {
      const routeName = state.route.name

      if (routeName === 'page') {
        let activePageSubNavigation = []
        if (getters.activeBottomBar && getters.activePage) {
          const activePageId = getters.activePage._id
          
          getters.activeBottomBar.forEach(parentNavigationItem => {
            if (parentNavigationItem.pageId === activePageId) {
              activePageSubNavigation = parentNavigationItem.children
            }
            
            if (parentNavigationItem.children) {
              parentNavigationItem.children.forEach(childNavigationItem => {
                if (childNavigationItem.pageId === activePageId) {
                  activePageSubNavigation = parentNavigationItem.children
                }
              })
            }
          })
        }
        return activePageSubNavigation
      } 
      
      // check the LIVE section for manually created navigation items
      if (routeName !== 'rest') {
        const liveSubNavItems = state.navigation[0].children.find(navItem => navItem.isLiveSection)
        let liveSectionNavItems = JSON.parse(JSON.stringify(liveSubNavItems.children))

        let hasNavItemLand = false
        let hasNavItemPackages = false
        let hasNavItemTownhouses = false
        let hasNavItemHouses = false
        let hasNavItemApartments = false
        let hasNavItemDwellings = false
        let hasNavItemTurnkey = false

        liveSectionNavItems.forEach(item => {
          if (item.customRoute && item.customRoute.includes('live/land')) {
            hasNavItemLand = true
          }

          if (item.customRoute && item.customRoute.includes('live/packages')) {
            hasNavItemPackages = true
          }

          if (item.customRoute && item.customRoute.includes('live/townhouses')) {
            hasNavItemTownhouses = true
          }

          if (item.customRoute && item.customRoute.includes('live/houses')) {
            hasNavItemHouses = true
          }
          
          if (item.customRoute && item.customRoute.includes('live/apartments')) {
            hasNavItemApartments = true
          }

          if (item.customRoute && item.customRoute.includes('live/dwellings')) {
            hasNavItemDwellings = true
          }

          if (item.customRoute && item.customRoute.includes('live/turnkey')) {
            hasNavItemTurnkey = true
          }
        })

        // add live nav items if not manually added
        if (!hasNavItemLand) {
          if (getters['property/livingOptionsLand'] && getters['property/livingOptionsLand'].length) {
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/land`,
              '_id': 'live-land',
              'label':{
                'en': rootState.styleTemplate['land-page']['label'] ? rootState.styleTemplate['land-page']['label'] : 'Land for sale'
              }
            })
          }
        }

        if (!hasNavItemTownhouses) {
          if (getters['property/livingOptionsTownhouses'] && getters['property/livingOptionsTownhouses'].length) {
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/townhouses`,
              '_id': 'live-townhouse',
              'label':{
                'en': 'Townhouses'
              }
            })
          }
        }

        if (!hasNavItemHouses) {
          if (getters['property/livingOptionsHouses'] && getters['property/livingOptionsHouses'].length) {
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/houses`,
              '_id': 'live-house',
              'label':{
                'en': 'Houses'
              }
            })
          }
        }

        if (!hasNavItemApartments) {
          if (getters['property/livingOptionsApartments'] && getters['property/livingOptionsApartments'].length) {
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/apartments`,
              '_id': 'live-apartment',
              'label':{
                'en': 'Apartments'
              }
            })
          }
        }
        
        if (!hasNavItemDwellings) {
          if (rootState.styleTemplate['dwelling-page']['active'] && getters['property/dwellings'] && getters['property/dwellings'].length) {
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/dwellings`,
              '_id': 'live-dwelling',
              'label':{
                'en': rootState.styleTemplate['dwelling-page']['label'] ? rootState.styleTemplate['dwelling-page']['label'] : 'Dwellings'
              }
            })
          }
        }

        if (!hasNavItemTurnkey) {
          
          if (rootState.styleTemplate['turnkey-page']['active']) {
            console.log('here')
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/turnkeys`,
              '_id': 'live-turnkeys',
              'label':{
                'en': rootState.styleTemplate['turnkey-page']['label'] ? rootState.styleTemplate['turnkey-page']['label'] : 'Turnkey Homes'
              }
            })
          }
        }

        if (!hasNavItemPackages) {
          if (rootState.styleTemplate['package-page']['active'] && getters['property/livingOptionsPackages'] && getters['property/livingOptionsPackages'].length) {
            liveSectionNavItems.push({
              'to': `/${getters.activeCategory}/live/packages`,
              '_id': 'live-packages',
              'label':{
                'en': rootState.styleTemplate['package-page']['label'] ? rootState.styleTemplate['package-page']['label'] : 'House & Land'
              }
            })
          }
        }

        return liveSectionNavItems
      }

      return []
    },

    activePage (state) {
      let activePage = null
      if (state.route.params.pageId) {
        activePage = _.find(state.pages, page => {
          return page._id === state.route.params.pageId
        })
      }
      return activePage
    },

    activeBackground (state, getters) {
      const routeName = state.route.name
      const config = state.config

      if (getters.activeBackgroundOverride) {
        return state.assetsPath + getters.activeBackgroundOverride
      }

      if (getters.activePage) {
        return state.assetsPath + getters.activePage.backgroundImage
      }

      if (getters.activePackageSlide) {
        return state.assetsPath + getters.activePackageSlide.backgroundImage
      }

      if (routeName === 'live') {
        return state.assetsPath + config.liveHereSelectionBackground
      }

      if (routeName === 'living-entry') {
        return state.assetsPath + config.liveHereBackground
      }
      
      if (routeName === 'masterplan') {
        return state.assetsPath + config.liveHereBackground
      }

      if (routeName === 'masterplan-livingoption' && getters['masterplan/activeLivingOption']) {
        return state.assetsPath + getters['masterplan/activeLivingOption'].backgroundImage
      }

      if (routeName === 'masterplan-subsectiongroup' && getters['masterplan/activeSubsectionGroup']) {
        return state.assetsPath + getters['masterplan/activeSubsectionGroup'].backgroundImage
      }

      if ((routeName === 'masterplan-subsection' || routeName === 'masterplan-property') && getters['masterplan/activeSubsection']) {
        return state.assetsPath + getters['masterplan/activeSubsection'].backgroundImage
      }

      if (routeName === 'living-option' && getters['property/activeLivingOption']) {
        return state.assetsPath + getters['property/activeLivingOption'].backgroundImage
      }

      if (routeName === 'live-masterplan-living-option' && getters['masterplan/activeLivingOption']) {
        return state.assetsPath + getters['masterplan/activeLivingOption'].backgroundImage
      }

      if (['subsection', 'property'].includes(routeName) && getters['property/activeSubsection']) {
        return state.assetsPath + getters['property/activeSubsection'].backgroundImage
      }

      if (['packages', 'dwellings', 'favourites'].includes(routeName)) {
        if (['package-modal', 'dwelling-modal', 'favourite-modal', 'favourites'].includes(state.route.from.name)) {
          return state.animations.animateBackground
        }
      }

      if (['package-modal', 'dwelling-modal', 'favourite-modal'].includes(routeName)) {
        return state.animations.animateBackground
      }

      return ''
    },

    activeSlides (state, getters) {
      const routeName = state.route.name
      const pathway = state.route.params.pathway

      if (getters.activePage && getters.activePage.interactiveSlides) {
        return getters.activePage.interactiveSlides
      }

      if (['living-entry'].includes(routeName)) {
        switch (pathway) {
          case 'land':
            const livingOptionLand = getters['property/livingOptionsLand'].filter(option => !option.isVisuallyHidden)
            return _.sortBy(livingOptionLand, livingOption => {
              return livingOption.marker.x
            })

          case 'townhouses':
            return _.sortBy(getters['property/livingOptionsTownhouses'], livingOption => {
              return livingOption.marker.x
            }) 

          case 'houses':
            return _.sortBy(getters['property/livingOptionsHouses'], livingOption => {
              return livingOption.marker.x
            }) 

          case 'apartments':
            return _.sortBy(getters['property/livingOptionsApartments'], livingOption => {
              return livingOption.marker.x
            }) 
        
          default:
            break;
        }
      }

      if (['living-option', 'subsection', 'property'].includes(routeName) && getters['property/activeLivingOption']) {
        const subsections = getters['property/activeSubsections']

        if (subsections && subsections.length >= 1) {
          return _.sortBy(subsections, subsection => {
            return subsection.marker.x
          })
        } else {
          return []
        }
      }

      return []
    },

    activeBackgroundOverride (state) {
      return state.activeBackgroundOverride
    },

    activeTagsOverride (state) {
      return state.activeTagsOverride
    },

    activePageTags (state, getters, rootState, rootGetters) {
      let activePageTags = []

      if (getters.activeTagsOverride !== null) {
        return getters.activeTagsOverride
      }

      if (
        state.route.name === 'living-entry' &&
        rootGetters['config'].liveHereMasterplanTags &&
        rootGetters['config'].liveHereMasterplanTags.length
      ) {
        return rootGetters['config'].liveHereMasterplanTags
      }

      if (
        state.route.name === 'living-option' &&
        rootGetters['property/activeLivingOption'] &&
        rootGetters['property/activeLivingOption'].tags
      ) {
        return rootGetters['property/activeLivingOption'].tags
      }

      if (
        state.route.name === 'live-masterplan-living-option' &&
        rootGetters['masterplan/activeLivingOption'] &&
        rootGetters['masterplan/activeLivingOption'].tags
      ) {
        return rootGetters['masterplan/activeLivingOption'].tags
      }

      if (
        state.route.name === 'subsection' &&
        rootGetters['property/activeSubsection'] &&
        rootGetters['property/activeSubsection'].tags
      ) {
        return rootGetters['property/activeSubsection'].tags
      }

      if (getters.activePage && getters.activePage.tags) {
        activePageTags = _.filter(getters.activePage.tags, tag => {
          return tag.type !== 'feature'
        })

        activePageTags = _.sortBy(activePageTags, tag => { 
          return tag.pos.x
        })
      }

      return activePageTags
    },

    activePackageSlide (state, getters) {
      const activePackage = getters.activePropertySlides.find(_package => _package._id === state.activePackageSlideId)

      if (activePackage) {
        return activePackage
      }

      return null
    },

    activePropertySlides,

    activePropertyModal (state, getters, rootState) {
      const routeName = rootState.route.name

      if (rootState.route.params.propertyId && (routeName === 'property' || routeName === 'masterplan-property')) {
        const modalId = rootState.route.params.propertyId
        
        const property = getters['property/properties'].find(property => property._id === modalId)

        const dwelling = property.dwelling

        const images = property.dwelling ? [...dwelling.floorplanImages, ...dwelling.galleryImages] : [...property.viewImages, ...property.galleryImages]
        const landPrice = property.price ? property.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : false 
        const dwellingPrice = property.dwelling && dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : false 


        return {
          _id: property._id,
          description: dwelling ? dwelling.description : '',
          features: dwelling ? dwelling.featureList : '',
          frontage: property.frontage,
          images: images,
          labelTitle: 'Number',
          label: property.label,
          price: dwellingPrice ? dwellingPrice : landPrice,
          landSize: property.size,
          dwellingSize: dwelling ? dwelling.size : '',
          status: _.kebabCase(property.status),
          dwelling: dwelling
        }
      }

      if (routeName === 'package-modal') {
        const modalId = rootState.route.params.packageId
        const _package = getters['property/livingOptionsPackages'].find(_package => _package._id === modalId)
        let images = _package.dwelling.floorplanImages

        if (_package.dwelling.facadeImages.length) {
          images.push(_package.dwelling.facadeImages[0])
        }

        images.push(..._package.dwelling.galleryImages)

        if (_package) {
          return {
            _id: _package._id,
            description: _package.dwelling.description,
            features: _package.dwelling.featureList,
            images: images,
            labelTitle: 'House Name',
            label: _package.label,
            price: _package.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) ,
            size: _package.property.size
          }
        } else {
          return {}
        }
      }

      if (routeName === 'dwelling-modal') {
        const modalId = rootState.route.params.dwellingId
        const dwelling = getters['property/dwellings'].find(dwelling => dwelling._id === modalId)

        if (dwelling) {
          return {
            _id: dwelling._id,
            description: dwelling.description,
            features: dwelling.featureList,
            images: [...dwelling.floorplanImages, ...dwelling.galleryImages],
            labelTitle: 'Name',
            label: dwelling.label,
            price: dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : '',
            dwellingSize: dwelling.size,
          }
        } else {
          return {}
        }
      }

      if (routeName === 'turnkey-modal') {
        const modalId = rootState.route.params.propertyId
        const turnkey = getters['property/turnkeys'].find(property => property._id === modalId)

        if (turnkey) {
          return {
            _id: turnkey._id,
            description: turnkey.description,
            features: turnkey.featureList,
            images: turnkey.images,
            labelTitle: 'Name',
            label: turnkey.name,
            price: turnkey.price ? turnkey.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : '',
            dwellingSize: turnkey.dwellingSize,
            landSize: turnkey.landSize,
          }
        } else {
          return {}
        }
      }

      if (routeName === 'favourite-modal') {
        const modalId = rootState.route.params.favouriteId
        const _package = getters['property/livingOptionsPackages'].find(_package => _package._id === modalId)
        const land = getters['property/land'].find(land => land._id === modalId)
        const dwelling = getters['property/dwellings'].find(dwelling => dwelling._id === modalId)
        const turnkey = getters['property/turnkeys'].find(property => property._id === modalId)

        if (_package) {
          return {
            _id: _package._id,
            description: _package.dwelling.description,
            features: _package.dwelling.featureList,
            images: [..._package.dwelling.floorplanImages, ..._package.dwelling.galleryImages],
            labelTitle: 'House Name',
            label: _package.label,
            price: _package.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }),
            size: _package.property.size
          }
        } else if (land) {
          return {
            _id: land._id,
            description: '',
            features: '',
            images: [...land.viewImages, ...land.galleryImages],
            labelTitle: 'Lot Number',
            label: land.label,
            price: land.price ? land.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : null,
            size: land.size
          }
        } else if (dwelling) {
          return {
            _id: dwelling._id,
            description: dwelling.description,
            features: dwelling.featureList,
            images: [...dwelling.floorplanImages, ...dwelling.galleryImages],
            labelTitle: 'Name',
            label: dwelling.label,
            price: dwelling.price ? dwelling.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : '',
            dwellingSize: dwelling.size,
          }
        } else if (turnkey) {
          return {
            _id: turnkey._id,
            description: turnkey.description,
            features: turnkey.featureList,
            images: turnkey.images,
            labelTitle: 'Name',
            label: turnkey.name,
            price: turnkey.price ? turnkey.price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumSignificantDigits: 10 }) : '',
            dwellingSize: turnkey.dwellingSize,
            landSize: turnkey.landSize,
          }
        } else {
          return {}
        }
      }

      return {}
    },

    showSimpleBacklink (state, getters, rootState) {
      const currentRoute = rootState.route.name
      return [
        'masterplan-livingoption',
        'masterplan-subsectiongroup',
        'masterplan-subsection',
        'masterplan-property'
      ].includes(currentRoute)   
    },

    activeBackLink (state, getters, rootState) {
      const activeLivingOption =  getters['property/activeLivingOption']
      const activeSubsection = getters['property/activeSubsection']
      const categoryId = rootState.route.params.categoryId
      const currentRoute = rootState.route.name
      const livingOptionId = rootState.route.params.livingOptionId
      const pathway = rootState.route.params.pathway
      const subsectionId = rootState.route.params.subsectionId

      if (currentRoute === 'living-option') {
        return `/category/${categoryId}/live/${pathway}`
      }

      if (currentRoute === 'property') {
        return `/category/${categoryId}/live/${pathway}/${livingOptionId}/subsection/${subsectionId}${'?activeSubsectionId=' + rootState.route.query.activeSubsectionId}`
      }

      if (currentRoute === 'subsection') {
        if (activeSubsection.parent.type === 'subsection') {
          return `/category/${categoryId}/live/${pathway}/${livingOptionId}/subsection/${activeSubsection.parent.id}${'?activeSubsectionId=' + rootState.route.query.activeSubsectionId}`
        }

        if (activeSubsection.parent.type === 'livingOption') {
          if (activeLivingOption.subsections.length === 1) {
            return `/category/${categoryId}/live/${pathway}`
          } else {
            return `/category/${categoryId}/live/${pathway}/${activeSubsection.parent.id}`
          }
        }
      }

      if (['favourites', 'favourite-modal'].includes(currentRoute)) {
        return rootState.favourites.favouritesBackLink
      }

      return null
    },

    config (state) {
      return state.config
    },

    navigation (state) {
      let navigation = JSON.parse(JSON.stringify(state.navigation))
      if (navigation && navigation.length) {
        navigation.forEach(navigationCategory => {
          navigationCategory.children = _.sortBy(navigationCategory.children, child => child.order)
        })
      }
      return navigation
    },

    language (state) {
      return state.language
    },

    languages (state) {
      return state.languages
    },

    appScale (state) {
      return state.appScale
    },

    styleTemplate (state) {
      return state.styleTemplate
    }
  },

  mutations: {
    SET_APP_SCALE (state, scale) {
      state.appScale = scale
    },

    SET_CONFIG (state, config) {
      state.config = config
    },

    SET_MATTERPORT_AVAILABILITY (state, is_available) {
      state.isMatterportAvailable = is_available
    },

    SET_NAVIGATION (state, navigation) {
      state.navigation = navigation
    },

    SET_PAGES (state, pages) {
      state.pages = pages
    },

    SET_LANGUAGE (state, language) {
      state.language = language
    },

    SET_ACTIVE_PACKAGE_SLIDE_ID (state, id) {
      state.activePackageSlideId = id
    },

    SET_ACTIVE_BACKGROUND_OVERRIDE (state, background) {
      state.activeBackgroundOverride = background
    },

    SET_ACTIVE_TAGS_OVERRIDE (state, tags) {
      state.activeTagsOverride = tags
    },

    SET_STYLE_TEMPLATE (state, styleTemplate) {
      state.styleTemplate = styleTemplate
    }
  },

  actions: {
    snitch ({ state }) {
      axios.get(`https://snitch.sensoreal.com.au/touch-seen/${state.deviceName}`).catch(e => {
        console.error(e)
      })
    },

    async fetchMatterportAvailability ({ state, commit }) {
      // check the cached state so we dont make unnecessary requests
      if (state.isMatterportAvailable !== null) {
        return state.isMatterportAvailable
      } else {
        await axios.get('https://my.matterport.com', {
          timeout: 5000,
        }).then(() => {
          commit('SET_MATTERPORT_AVAILABILITY', true)
        }).catch(e => {
          if (e.response && e.response.status == 403) {
            commit('SET_MATTERPORT_AVAILABILITY', true)
          } else {
            commit('SET_MATTERPORT_AVAILABILITY', true)
          }
        }).finally(() => {
          return state.isMatterportAvailable
        })
      }
    },

    async fetchData ({ commit }) {
      await axios.get(configData).then(res => {
        const configItems = res.data.config
        // console.log(configItems)
        let configObject = {}
        configItems.map(el => {
          configObject[el.key] = el.value
        })
        // console.log(configObject)
        commit('SET_CONFIG', configObject)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(navigationData).then(res => {
        commit('SET_NAVIGATION', res.data.categories)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(pagesData).then(res => {
        commit('SET_PAGES', res.data)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(livingOptionsData).then(res => {
        commit('property/SET_LIVING_OPTIONS', res.data)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(subsectionsData).then(res => {
        commit('property/SET_SUBSECTIONS', res.data)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(subsectionGroupsData).then(res => {
        commit('property/SET_SUBSECTION_GROUPS', res.data)
      }).catch(err => {
        console.warn(err)
      })
      
      await axios.get(propertiesData).then(res => {
        commit('property/SET_PROPERTIES', res.data)
      }).catch(err => {
        console.warn(err)
      })
      
      await axios.get(dwellingsData).then(res => {
        commit('property/SET_DWELLINGS', res.data)
      }).catch(err => {
        console.warn(err)
      })
      
      await axios.get(packagesData).then(res => {
        commit('property/SET_PACKAGES', res.data)
      }).catch(err => {
        console.warn(err)
      })

      await axios.get(turnkeysData).then(res => {
        commit('property/SET_TURNKEYS', res.data)
      }).catch(err => {
        console.warn(err)
      })
    },

    toggleBottomBarVisibility ({ state }) {
      // TODO: dispatch a mutation that sets the bar as visible
      state.isBottomBarVisible = !state.isBottomBarVisible
    },

    triggerLightswarm ({ state }, { type, label }) {
      if (state.lightswarmHost) {
        axios.get(`${state.lightswarmHost}/${type}/${label}`)
      }
    },

    triggerLightswarmAllOn ({ state }) {
      if (state.lightswarmHost) {
        axios.get(`${state.lightswarmHost}/all-on`)
      }
    },    

    triggerIframe ({ commit, state }, url) {
      if (state.videoWallHost && url) {
        // console.log(`GET ${process.env.VUE_APP_VIDEOWALL_HOST}/index.php?x=${videoWallTrigger}`)
        if (state.videoWallIframePlaying) {
          state.videoWallIframePlaying = false
          commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', null)
          axios.post(`${state.videoWallHost}/iframe`, { url: null })
        } else {
          state.videoWallIframePlaying = true
          commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', url)
          axios.post(`${state.videoWallHost}/iframe`, { url })
        }
      } else {
        if (state.videoWallIframePlaying && url) {
          state.videoWallIframePlaying = false
          commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', null)
        } else {
          state.videoWallIframePlaying = true
          commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', url)
        }        
        
      }
    },

    triggerIframeLocal ({ commit, state }, url) {
      if (url) {
        if (state.localIframePlaying) {
          state.localIframePlaying = false
          commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', null)
        } else {
          state.localIframePlaying = true
          commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', url)
        }
      }
    },
    
    triggerVideo ({ state }, videoWallTrigger) {
      if (state.triggerVideos && process.env.VUE_APP_VIDEOWALL_HOST && videoWallTrigger) {
        // console.log(`GET ${process.env.VUE_APP_VIDEOWALL_HOST}/index.php?x=${videoWallTrigger}`)
        axios.get(`${process.env.VUE_APP_VIDEOWALL_HOST}/index.php?x=${videoWallTrigger}`)
      }
    }
  }
})
